import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ComponentCard from '../../components/common/ComponentCard';
import DashboardMetricCard, {
  tabPageMetricCardClassName,
} from '../../components/common/DashboardMetricCard';
import PageMeta from '../../components/common/PageMeta';
import TableEmptyState from '../../components/common/TableEmptyState';
import { Dropdown } from '../../components/ui/dropdown/Dropdown';
import { DropdownItem } from '../../components/ui/dropdown/DropdownItem';
import {
  Table,
  TableBadge,
  TableBody,
  TableCell,
  TableErrorState,
  TableHeader,
  TableLoadingState,
  TableRow,
  TableShell,
  TableSortButton,
} from '../../components/ui/table';
import { useModal } from '../../hooks/useModal';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import { formatShortDateTime } from '../../shared/time';
import { api, apiEndpoints } from '../../utils/apiService';
import AddProspectModal from './AddProspectModal';
import CsvImportModal from './CsvImportModal';
import {
  CALL_OUTCOME_TONES,
  PROSPECT_STATUSES,
  ProspectListItem,
  ProspectListResponse,
  ProspectStatus,
  contactDisplayName,
  formatOutcome,
  formatPercent,
  prospectStatusMeta,
} from './types';

const PAGE_SIZE = 20;
const DEFAULT_ORDERING = '-created_at';

const SORTABLE_FIELDS = new Set([
  'name',
  'status',
  'market',
  'created_at',
  'last_contacted_at',
  'audit_call_count',
  'last_call_date',
  'latest_missed_call_rate',
]);

const ProspectsIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
    />
  </svg>
);

const PhoneStatIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
    />
  </svg>
);

const MailStatIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
    />
  </svg>
);

async function fetchProspects(
  page: number,
  ordering: string,
  search: string,
  statuses: ProspectStatus[],
): Promise<ProspectListResponse> {
  const response = await api.get(
    apiEndpoints.getSalesProspects({
      page,
      page_size: PAGE_SIZE,
      ordering,
      q: search || undefined,
      status: statuses.length > 0 ? statuses.join(',') : undefined,
    }),
  );
  if (!response || !response.ok) {
    const text = response ? await response.text() : '';
    throw new Error(
      `Failed to fetch prospects${response ? ` (status ${response.status})` : ''}. ${text.substring(0, 120)}`,
    );
  }
  return response.json();
}

const ProspectsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const orderingParam = searchParams.get('ordering') ?? DEFAULT_ORDERING;
  const statusFilterParam = searchParams.get('status') ?? '';
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);

  const addModal = useModal();
  const importModal = useModal();
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const statusFilters = useMemo(() => {
    const validStatuses = new Set(PROSPECT_STATUSES.map((s) => s.value));
    return statusFilterParam
      .split(',')
      .map((status) => status.trim())
      .filter((status): status is ProspectStatus =>
        validStatuses.has(status as ProspectStatus),
      );
  }, [statusFilterParam]);

  const queryKey = useScopedQueryKey(
    'sales-prospects',
    page,
    orderingParam,
    debouncedSearch,
    statusFilters.join(','),
  );

  const { data, isPending, isError, error, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchProspects(page, orderingParam, debouncedSearch, statusFilters),
    placeholderData: keepPreviousData,
  });

  const { sortKey, sortOrder } = useMemo(() => {
    const desc = orderingParam.startsWith('-');
    const field = desc ? orderingParam.slice(1) : orderingParam;
    return {
      sortKey: field,
      sortOrder: (desc ? 'desc' : 'asc') as 'asc' | 'desc',
    };
  }, [orderingParam]);

  const toggleSort = (field: string) => {
    if (!SORTABLE_FIELDS.has(field)) return;
    setPage(1);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const prevRaw = prev.get('ordering') ?? DEFAULT_ORDERING;
      const prevDesc = prevRaw.startsWith('-');
      const prevField = prevDesc ? prevRaw.slice(1) : prevRaw;
      const newDesc = prevField !== field ? false : !prevDesc;
      next.set('ordering', newDesc ? `-${field}` : field);
      return next;
    });
  };

  const setStatusFilters = (statuses: ProspectStatus[]) => {
    setPage(1);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (statuses.length > 0) {
        next.set('status', statuses.join(','));
      } else {
        next.delete('status');
      }
      return next;
    });
  };

  const toggleStatusFilter = (status: ProspectStatus) => {
    const nextStatuses = statusFilters.includes(status)
      ? statusFilters.filter((s) => s !== status)
      : [...statusFilters, status];
    setStatusFilters(nextStatuses);
  };

  const prospects = data?.results ?? [];
  const totalPages = data?.total_pages ?? 1;
  const totalCount = data?.count ?? 0;
  const statusCounts = data?.status_counts ?? {};
  const allCount = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  const selectedStatusSummary =
    statusFilters.length === 0
      ? `All statuses (${allCount})`
      : statusFilters.length === 1
        ? `${prospectStatusMeta(statusFilters[0]).label} (${statusCounts[statusFilters[0]] ?? 0})`
        : `${statusFilters.length} statuses`;
  const showInitialLoading = isPending && prospects.length === 0;
  const fetchError =
    isError && error instanceof Error
      ? error.message
      : isError
        ? 'An unexpected error occurred while fetching prospects.'
        : null;

  const auditedCount =
    (statusCounts['audited'] ?? 0) +
    (statusCounts['draft_ready'] ?? 0) +
    (statusCounts['approved'] ?? 0) +
    (statusCounts['pushed'] ?? 0) +
    (statusCounts['active'] ?? 0) +
    (statusCounts['replied'] ?? 0);
  const draftsAwaiting = statusCounts['draft_ready'] ?? 0;
  const pushedOrActive =
    (statusCounts['pushed'] ?? 0) +
    (statusCounts['active'] ?? 0) +
    (statusCounts['replied'] ?? 0);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['sales-prospects'] });
  };

  const handleStartAudit = async (prospect: ProspectListItem) => {
    setActionMessage(null);
    try {
      const response = await api.post(
        apiEndpoints.startSalesProspectAudit(prospect.id),
      );
      if (!response) {
        setActionMessage(
          `Could not start audit for ${prospect.name}: no response.`,
        );
        return;
      }
      const body = await response.json();
      if (!response.ok) {
        setActionMessage(
          `Could not start audit for ${prospect.name}: ${body.error || response.status}`,
        );
        return;
      }
      setActionMessage(`Audit call queued for ${prospect.name}.`);
      refresh();
    } catch (err) {
      setActionMessage(
        `Could not start audit for ${prospect.name}: ${err instanceof Error ? err.message : 'unknown error'}`,
      );
    }
  };

  const hasNextPage = page < totalPages;
  const hasPrevPage = page > 1;
  const showPagination =
    !showInitialLoading && !fetchError && prospects.length > 0;

  return (
    <>
      <PageMeta
        title="AskArthur | Sales Prospects"
        description="Outbound restaurant prospecting"
      />

      <div className="space-y-5 md:space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="app-page-title">Sales Prospects</h1>
            <p className="app-page-subtitle mt-1.5">
              {totalCount.toLocaleString()} prospects
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                type="button"
                onClick={() => setAddMenuOpen((prev) => !prev)}
                aria-haspopup="menu"
                aria-expanded={addMenuOpen}
                className="flex items-center gap-1.5 rounded-2xl bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
              >
                Add prospect(s)
                <svg
                  className={`h-4 w-4 transition-transform ${addMenuOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                  />
                </svg>
              </button>
              <Dropdown
                isOpen={addMenuOpen}
                onClose={() => setAddMenuOpen(false)}
                className="w-48 p-1.5"
              >
                <DropdownItem
                  onClick={addModal.openModal}
                  onItemClick={() => setAddMenuOpen(false)}
                  className="rounded-lg dark:text-gray-300 dark:hover:bg-white/5"
                >
                  Add prospect
                </DropdownItem>
                <DropdownItem
                  onClick={importModal.openModal}
                  onItemClick={() => setAddMenuOpen(false)}
                  className="rounded-lg dark:text-gray-300 dark:hover:bg-white/5"
                >
                  Import CSV
                </DropdownItem>
              </Dropdown>
            </div>
            <Link
              to="/sales/settings"
              className="app-button-secondary flex items-center gap-1.5 rounded-2xl px-4 py-2 text-sm font-medium"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.02-.397-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.28z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Settings
            </Link>
          </div>
        </div>

        <div className="flex flex-col gap-3.5 sm:flex-row sm:flex-wrap sm:items-start">
          <DashboardMetricCard
            className={`${tabPageMetricCardClassName} max-w-[230px]`}
            icon={<ProspectsIcon />}
            value={allCount.toLocaleString()}
            label="Total Prospects"
            badgeLabel="Prospects"
            compact
            loading={showInitialLoading}
            colorClass="bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
            iconBgClass="bg-blue-100 dark:bg-blue-900/30"
          />
          <DashboardMetricCard
            className={`${tabPageMetricCardClassName} max-w-[230px]`}
            icon={<PhoneStatIcon />}
            value={auditedCount.toLocaleString()}
            label="Audited"
            badgeLabel="Audits"
            compact
            loading={showInitialLoading}
            colorClass="bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400"
            iconBgClass="bg-amber-100 dark:bg-amber-900/30"
          />
          <DashboardMetricCard
            className={`${tabPageMetricCardClassName} max-w-[230px]`}
            icon={<MailStatIcon />}
            value={draftsAwaiting.toLocaleString()}
            label="Drafts Awaiting Approval"
            badgeLabel="Drafts"
            compact
            loading={showInitialLoading}
            colorClass="bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400"
            iconBgClass="bg-purple-100 dark:bg-purple-900/30"
          />
          <DashboardMetricCard
            className={`${tabPageMetricCardClassName} max-w-[230px]`}
            icon={<MailStatIcon />}
            value={pushedOrActive.toLocaleString()}
            label="Pushed / Active"
            badgeLabel="Campaigns"
            compact
            loading={showInitialLoading}
            colorClass="bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400"
            iconBgClass="bg-green-100 dark:bg-green-900/30"
          />
        </div>

        {actionMessage && (
          <div className="rounded-2xl border border-[rgb(var(--app-border)/0.6)] bg-[rgb(var(--app-surface-muted)/0.8)] px-4 py-3 text-sm text-[rgb(var(--app-text))]">
            {actionMessage}
          </div>
        )}

        <ComponentCard
          title="Prospects"
          className="app-panel rounded-[28px] border-0 bg-transparent shadow-none"
        >
          <div className="relative w-full sm:w-auto">
            <button
              type="button"
              onMouseDown={(event) => event.stopPropagation()}
              onClick={() => setStatusMenuOpen((prev) => !prev)}
              aria-haspopup="menu"
              aria-expanded={statusMenuOpen}
              className="app-input flex h-11 w-full items-center justify-between gap-3 rounded-2xl px-4 py-2.5 text-left text-sm sm:w-72"
            >
              <span>
                <span className="font-medium text-[rgb(var(--app-text))]">
                  Status
                </span>
                <span className="ml-2 text-[rgb(var(--app-muted))]">
                  {selectedStatusSummary}
                </span>
              </span>
              <svg
                className={`h-4 w-4 shrink-0 text-[rgb(var(--app-muted))] transition-transform ${
                  statusMenuOpen ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            </button>
            <Dropdown
              isOpen={statusMenuOpen}
              onClose={() => setStatusMenuOpen(false)}
              className="left-0 right-auto w-72 p-2"
            >
              <div className="mb-1 flex items-center justify-between border-b border-[rgb(var(--app-border)/0.55)] px-2 pb-2">
                <span className="text-xs font-semibold uppercase tracking-[0.08em] text-[rgb(var(--app-muted))]">
                  Filter by status
                </span>
                <button
                  type="button"
                  onClick={() => setStatusFilters([])}
                  disabled={statusFilters.length === 0}
                  className="text-xs font-medium text-[rgb(var(--app-brand-strong))] disabled:cursor-not-allowed disabled:text-[rgb(var(--app-muted))]"
                >
                  Clear all
                </button>
              </div>
              {PROSPECT_STATUSES.map((s) => {
                const isSelected = statusFilters.includes(s.value);
                return (
                  <button
                    key={s.value}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={isSelected}
                    onClick={() => toggleStatusFilter(s.value)}
                    className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-sm text-[rgb(var(--app-text))] hover:bg-[rgb(var(--app-surface-muted)/0.8)]"
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className={`flex h-4 w-4 items-center justify-center rounded border ${
                          isSelected
                            ? 'border-[rgb(var(--app-brand))] bg-[rgb(var(--app-brand))] text-white'
                            : 'border-[rgb(var(--app-border))] bg-transparent'
                        }`}
                      >
                        {isSelected && (
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M4.5 12.75l6 6 9-13.5"
                            />
                          </svg>
                        )}
                      </span>
                      {s.label}
                    </span>
                    <span className="text-xs text-[rgb(var(--app-muted))]">
                      {statusCounts[s.value] ?? 0}
                    </span>
                  </button>
                );
              })}
            </Dropdown>
          </div>

          <div className="flex flex-col gap-2 sm:max-w-sm">
            <label
              htmlFor="prospects-search"
              className="text-xs font-semibold uppercase tracking-[0.08em] text-[rgb(var(--app-muted))]"
            >
              Search by name, email, phone, market, or city
            </label>
            <input
              id="prospects-search"
              type="search"
              placeholder="Search prospects..."
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
              className="app-input h-11 w-full rounded-2xl px-4 py-2.5 text-sm"
            />
          </div>

          <TableShell
            minWidth={1200}
            isRefreshing={
              isFetching &&
              !showInitialLoading &&
              !fetchError &&
              prospects.length > 0
            }
            refreshLabel="Updating prospects..."
          >
            {showInitialLoading ? (
              <TableLoadingState label="Loading prospects…" />
            ) : fetchError ? (
              <TableErrorState message={fetchError} />
            ) : prospects.length === 0 ? (
              <TableEmptyState itemName="prospects" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableCell isHeader>
                      <TableSortButton
                        label="Name"
                        isActive={sortKey === 'name'}
                        direction={sortOrder}
                        onClick={() => toggleSort('name')}
                      />
                    </TableCell>
                    <TableCell isHeader>Contact</TableCell>
                    <TableCell isHeader>Phone</TableCell>
                    <TableCell isHeader>
                      <TableSortButton
                        label="Market"
                        isActive={sortKey === 'market'}
                        direction={sortOrder}
                        onClick={() => toggleSort('market')}
                      />
                    </TableCell>
                    <TableCell isHeader>
                      <TableSortButton
                        label="Status"
                        isActive={sortKey === 'status'}
                        direction={sortOrder}
                        onClick={() => toggleSort('status')}
                      />
                    </TableCell>
                    <TableCell isHeader align="center">
                      <TableSortButton
                        label="Calls"
                        isActive={sortKey === 'audit_call_count'}
                        direction={sortOrder}
                        onClick={() => toggleSort('audit_call_count')}
                      />
                    </TableCell>
                    <TableCell isHeader align="center">
                      <TableSortButton
                        label="Missed %"
                        isActive={sortKey === 'latest_missed_call_rate'}
                        direction={sortOrder}
                        onClick={() => toggleSort('latest_missed_call_rate')}
                      />
                    </TableCell>
                    <TableCell isHeader>
                      <TableSortButton
                        label="Last Call"
                        isActive={sortKey === 'last_call_date'}
                        direction={sortOrder}
                        onClick={() => toggleSort('last_call_date')}
                      />
                    </TableCell>
                    <TableCell isHeader>
                      <TableSortButton
                        label="Last Contacted"
                        isActive={sortKey === 'last_contacted_at'}
                        direction={sortOrder}
                        onClick={() => toggleSort('last_contacted_at')}
                      />
                    </TableCell>
                    <TableCell isHeader align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {prospects.map((prospect) => {
                    const statusMeta = prospectStatusMeta(prospect.status);
                    return (
                      <TableRow key={prospect.id} interactive>
                        <TableCell className="font-medium text-[rgb(var(--app-text))]">
                          <Link
                            to={`/sales/prospects/${prospect.id}`}
                            className="hover:text-[rgb(var(--app-brand-strong))]"
                          >
                            <span className="block max-w-[180px] truncate font-semibold">
                              {prospect.name}
                            </span>
                            {prospect.restaurant_group_name && (
                              <span className="mt-0.5 block max-w-[180px] truncate text-[11px] text-[rgb(var(--app-muted))]">
                                {prospect.restaurant_group_name}
                              </span>
                            )}
                            {prospect.do_not_contact && (
                              <span className="mt-0.5 block text-[11px] text-error-500">
                                Suppressed
                              </span>
                            )}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[180px]">
                            <div className="truncate text-sm text-[rgb(var(--app-text))]">
                              {contactDisplayName(prospect) || '—'}
                            </div>
                            <div className="truncate text-xs text-[rgb(var(--app-muted))]">
                              {prospect.contact_email || '—'}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{prospect.phone || '—'}</TableCell>
                        <TableCell>
                          <div className="max-w-[140px]">
                            <div className="truncate text-sm text-[rgb(var(--app-text))]">
                              {prospect.market || prospect.city || '—'}
                            </div>
                            {prospect.city && prospect.market && (
                              <div className="truncate text-xs text-[rgb(var(--app-muted))]">
                                {prospect.city}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <TableBadge tone={statusMeta.tone}>
                            {statusMeta.label}
                          </TableBadge>
                        </TableCell>
                        <TableCell align="center">
                          {prospect.audit_call_count > 0 ? (
                            <TableBadge tone="info">
                              {prospect.audit_call_count}
                            </TableBadge>
                          ) : (
                            <span className="text-[rgb(var(--app-muted)/0.8)]">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {formatPercent(prospect.latest_missed_call_rate)}
                        </TableCell>
                        <TableCell>
                          {prospect.last_call_date ? (
                            <div>
                              <div className="text-sm text-[rgb(var(--app-text))]">
                                {prospect.last_call_date}
                              </div>
                              <TableBadge
                                tone={
                                  CALL_OUTCOME_TONES[
                                    prospect.last_call_outcome ?? ''
                                  ] ?? 'neutral'
                                }
                              >
                                {formatOutcome(prospect.last_call_outcome)}
                              </TableBadge>
                            </div>
                          ) : (
                            <span className="text-[rgb(var(--app-muted)/0.8)]">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {prospect.last_contacted_at
                            ? formatShortDateTime(prospect.last_contacted_at)
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleStartAudit(prospect)}
                              disabled={
                                prospect.do_not_contact || !prospect.phone
                              }
                              className={`app-chip text-[11px] ${
                                prospect.do_not_contact || !prospect.phone
                                  ? 'cursor-not-allowed opacity-45'
                                  : 'hover:border-[rgb(var(--app-brand)/0.24)] hover:text-[rgb(var(--app-brand-strong))]'
                              }`}
                              title={
                                prospect.do_not_contact
                                  ? 'Prospect is suppressed'
                                  : !prospect.phone
                                    ? 'Prospect has no phone'
                                    : 'Queue an audit call'
                              }
                            >
                              Audit
                            </button>
                            <Link
                              to={`/sales/prospects/${prospect.id}?tab=competitors`}
                              className="app-chip text-[11px] hover:border-[rgb(var(--app-brand)/0.24)] hover:text-[rgb(var(--app-brand-strong))]"
                              title={
                                prospect.competitor_count > 0
                                  ? `${prospect.competitor_count} competitor${
                                      prospect.competitor_count === 1 ? '' : 's'
                                    } discovered`
                                  : 'View competitor analysis'
                              }
                            >
                              Competitors
                              {prospect.competitor_count > 0
                                ? ` (${prospect.competitor_count})`
                                : ''}
                            </Link>
                            <Link
                              to={`/sales/prospects/${prospect.id}`}
                              className="app-chip text-[11px] hover:border-[rgb(var(--app-brand)/0.24)] hover:text-[rgb(var(--app-brand-strong))]"
                            >
                              View
                            </Link>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </TableShell>

          {showPagination && (
            <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <span className="block text-sm text-[rgb(var(--app-muted))]">
                Showing page {page} of {totalPages} ({totalCount} prospects)
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    hasPrevPage && setPage((p) => Math.max(p - 1, 1))
                  }
                  disabled={!hasPrevPage}
                  className={`app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium ${
                    hasPrevPage ? '' : 'cursor-not-allowed opacity-45'
                  }`}
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => hasNextPage && setPage((p) => p + 1)}
                  disabled={!hasNextPage}
                  className={`app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium ${
                    hasNextPage ? '' : 'cursor-not-allowed opacity-45'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </ComponentCard>
      </div>

      <AddProspectModal
        isOpen={addModal.isOpen}
        onClose={addModal.closeModal}
        onCreated={refresh}
      />
      <CsvImportModal
        isOpen={importModal.isOpen}
        onClose={importModal.closeModal}
        onImported={refresh}
      />
    </>
  );
};

export default ProspectsPage;
